import { Directive, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

@Directive()
export class FileOrImageSelectorComponent<T> {
	@Input()
	allowedFileTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
	@Input()
	hideUploadOnMaxAmount: boolean = false;
	@Input()
	files: T[] | null;
	@Input()
	LABELS: FileSelectorLabels | null;
	@Input()
	maxAmount: number;
	@Input()
	maxFileSizeInMb: number = 3;
	@Input()
	required: boolean = false;
	@Input()
	showErrors: boolean = false;

	@Output()
	add: EventEmitter<any> = new EventEmitter<any>();
	@Output()
	delete: EventEmitter<T> = new EventEmitter<T>();

	@ViewChild('uploadInput')
	uploadInput: ElementRef;

	dragging: boolean = false;
	error: string | null | undefined = null;

	get acceptedFileTypes(): string {
		return this.allowedFileTypes.join(',');
	}

	get currentFiles(): T[] {
		return this.files || [];
	}

	get hideUpload(): boolean {
		if (this.hideUploadOnMaxAmount) {
			return this.currentFiles.length >= this.maxAmount;
		}

		return false;
	}

	getSize(file: File): string {
		return this.formatSize(file.size);
	}

	hasError(): boolean {
		const total = this.currentFiles.length;
		return this.required && total < 1;
	}

	fileSelect(event: any) {
		const MAX_FILE_SIZE = 1000 * 1000 * this.maxFileSizeInMb;

		this.dragging = false;
		this.error = null;

		const file: File = event.target.files[0];

		if (file) {
			const allowExtension = this.allowFileExtension(file);

			if (!allowExtension) {
				this.error = this.LABELS?.ERROR_EXTENSION;
				return;
			}

			const alreadyExists = this.fileAlreadyExists(file);

			if (alreadyExists) {
				this.error = this.LABELS?.ERROR_EXISTS;
				return;
			}

			const sizeTooLarge = file.size > MAX_FILE_SIZE;

			if (sizeTooLarge) {
				this.error = this.LABELS?.ERROR_SIZE;
				return;
			}

			const total = this.filesAmount();

			if (total >= this.maxAmount) {
				this.error = this.LABELS?.ERROR_AMOUNT;
				return;
			}

			this.emitUpload(file);

			this.uploadInput.nativeElement.value = null;
		}
	}

	removeFile(file: T, event?: any) {
		if (event) {
			event.preventDefault();
		}

		this.error = null;
		this.delete.emit(file);
	}

	protected allowFileExtension(file: File): boolean {
		const allow = this.allowedFileTypes.find((t) => t === file.type);
		return !!allow;
	}

	protected emitUpload(file: File) {
		this.add.emit(file);
	}

	protected fileAlreadyExists(file: File): boolean {
		return false;
	}

	protected filesAmount(): number {
		return this.currentFiles.length;
	}

	protected formatSize(value: number, rest: number = 0, loop: number = 0): string {
		if (value > 1000) {
			const r = value % 1000;
			const v = (value - r) / 1000;

			return this.formatSize(v, r, loop + 1);
		}

		const unit = this.getUnit(loop);

		if (rest >= 100) {
			const r = rest % 100;
			const v = (rest - r) / 100;

			return `${value}.${v} ${unit}`;
		}

		return `${value} ${unit}`;
	}

	protected getUnit(loop: number) {
		switch (loop) {
			case 1:
				return 'kB';
			case 2:
				return 'MB';
			case 3:
				return 'GB';
			case 4:
				return 'TB';
		}

		return 'B';
	}
}

export interface FileSelectorLabels {
	ADD_BUTTON: string;
	ERROR_AMOUNT: string;
	ERROR_EXISTS: string;
	ERROR_EXTENSION: string;
	ERROR_REQUIRED: string;
	ERROR_SIZE: string;
	TEXT: string;
	TITLE?: string;
}
